import { authStore, signedOutBy } from "@/convex/auth";
import { convex } from "@/convex/client";
import { readerIdOf, sessionStore } from "@/convex/session";
import { logging } from "@/logging/backend";
import { openReaderDb } from "@/storage/db";
import { legacyCleanup } from "@/storage/legacy-cleanup";
import { createShelf } from "@/library/shelf";
import { convexSyncBackend } from "./backend";
import { createBookmarkSync } from "./bookmarks";
import { createFluencyStore } from "./fluency";
import { forgetReader } from "./forget-reader";
import { localReaderOf, offlineReader } from "./offline-reader";
import { createOutbox, pruneHeldTaps, pruneQuarantine } from "./outbox";
import { installSyncTriggers } from "./triggers";
import { createPersonalReadingStore } from "./personal-readings";

export { clearOfflineReader } from "./offline-reader";

const logger = logging.createClientLogger();
const backend = convexSyncBackend(convex);
const currentReaderId = () => readerIdOf(sessionStore.getState());
const localReaderId = () => localReaderOf(sessionStore.getState());
const signedOut = (error: unknown) => signedOutBy(error);
const onReaderMismatch = () => void sessionStore.refresh();

export const readerDb = openReaderDb();

export const outbox = createOutbox({
  db: readerDb,
  backend,
  currentReaderId,
  signedOut,
  logger,
  onAcknowledged: () => void fluency.syncChangedSince(),
  onHistoriesInDoubt: (readerId, words) =>
    void fluency.refreshWords(readerId, words),
  onReaderMismatch,
});

export const fluency = createFluencyStore({
  db: readerDb,
  backend,
  outbox,
  currentReaderId,
  localReaderId,
  logger,
  onReaderMismatch,
});

export const bookmarks = createBookmarkSync({
  db: readerDb,
  backend,
  currentReaderId,
  localReaderId,
  signedOut,
  logger,
  onReaderMismatch,
});

export const shelf = createShelf(convex, currentReaderId);

export const personalReadings = createPersonalReadingStore({ db: readerDb });

/** What "Not me" on a bookmark and reset progress wipe locally. */
export const forgetLocalReader = (readerId: string): Promise<void> =>
  forgetReader(readerDb, readerId, localStorage, offlineReader, (error) => {
    logger.error("sync.assignment.error", error, { stage: "forget_reader" });
  });

function afterFirstPaint(run: () => void): void {
  if (typeof requestIdleCallback === "function") requestIdleCallback(run);
  else setTimeout(run, 0);
}

/** Boot: install the flush triggers, then tidy storage once the page is up. */
export function startLocalStore(): () => void {
  void bookmarks.loadClockSkew();
  const stop = installSyncTriggers({
    outbox,
    bookmarks,
    fluency,
    authStore,
    sessionStore,
    offlineReader,
    convex,
  });
  afterFirstPaint(() => {
    void legacyCleanup({ db: readerDb, logger });
    void pruneQuarantine(readerDb, Date.now());
    void pruneHeldTaps(readerDb, Date.now());
    void outbox.expireQueuedRows();
  });
  return stop;
}
